// /education/documents — Wave 70: TH hub of education document families
import { createFileRoute, Link } from "@tanstack/react-router";
import ServiceAuthorityBlock from "@/components/ServiceAuthorityBlock";
import { ctaDesc, ctaTitle } from "@/lib/seo";

const SITE = "https://nyclanguageinstitute.com";
const PATH = "/education/documents";
const URL = `${SITE}${PATH}`;
const EN_URL = `${SITE}/en/education/documents`;
const TITLE = "เอกสารการศึกษา แปล รับรอง เทียบวุฒิ 6 กรณี | NYC";
const DESC =
  "รวมเอกสารการศึกษาไทย–ต่างประเทศ 6 กรณี: ปริญญาบัตรและทรานสคริปต์เพื่อเรียนต่อ เทียบวุฒิจากต่างประเทศ เอกสารโรงเรียน ปพ.1 ปพ.7 ชุดวีซ่านักเรียน ใบประกอบวิชาชีพ และการรับรองคำแปล";

export const Route = createFileRoute("/education/documents/")({
  loader: async () => {
    const { EDUCATION_DOC_GROUPS, EDU_APOSTILLE_TH, EDU_AUTHORITY_TH } = await import("@/data/education-doc-groups");
    return {
      __d: { EDUCATION_DOC_GROUPS, EDU_APOSTILLE_TH, EDU_AUTHORITY_TH },
      groups: EDUCATION_DOC_GROUPS.map((g) => ({
        slug: g.slug,
        title: g.titleTh,
        faqs: g.faqsTh.map((f) => ({ q: f.q, a: f.a })),
      })),
    };
  },
  head: ({ loaderData }) => ({
    meta: [
      { title: ctaTitle(TITLE, PATH, "th") },
      { name: "description", content: ctaDesc(DESC, PATH, "th") },
      { property: "og:title", content: ctaTitle(TITLE, PATH, "th") },
      { property: "og:description", content: ctaDesc(DESC, PATH, "th") },
      { property: "og:url", content: URL },
      { property: "og:type", content: "website" },
      { property: "og:locale", content: "th_TH" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [
      { rel: "canonical", href: URL },
      { rel: "alternate", hrefLang: "th-TH", href: URL },
      { rel: "alternate", hrefLang: "en", href: EN_URL },
      { rel: "alternate", hrefLang: "x-default", href: URL },
    ],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          "@id": `${URL}#faq`,
          inLanguage: "th",
          mainEntity: (loaderData?.groups ?? []).flatMap((g) =>
            g.faqs.map((f) => ({
              "@type": "Question",
              name: f.q,
              acceptedAnswer: { "@type": "Answer", text: f.a },
            })),
          ),
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "ItemList",
          "@id": `${URL}#groups`,
          name: "กรณีเอกสารการศึกษาไทย–ต่างประเทศ",
          numberOfItems: (loaderData?.groups ?? []).length,
          itemListElement: (loaderData?.groups ?? []).map((g, i) => ({
            "@type": "ListItem",
            position: i + 1,
            name: g.title,
            url: `${URL}/${g.slug}`,
          })),
        }),
      },
    ],
  }),
  component: EducationDocumentsPage,
});

function EducationDocumentsPage() {
  const { __d: { EDUCATION_DOC_GROUPS, EDU_APOSTILLE_TH, EDU_AUTHORITY_TH } } = Route.useLoaderData() as { __d: typeof import("@/data/education-doc-groups") };
  return (
    <main className="mx-auto max-w-4xl px-4 py-10">
      <nav className="mb-4 text-sm text-muted-foreground">เอกสารการศึกษา / เอกสารตามกรณี</nav>

      <h1 className="text-3xl font-bold tracking-tight md:text-4xl">เอกสารการศึกษาไทย–ต่างประเทศ 6 กรณี</h1>

      <p className="mt-4 text-base leading-8 text-muted-foreground" data-speakable>
        เอกสารการศึกษาแต่ละกรณีเดินคนละเส้นทาง ขึ้นกับว่าใครเป็นผู้ออกเอกสารและใครเป็นผู้รับปลายทาง
        หน้านี้แยกงานออกเป็น 6 กรณี พร้อมรายการเอกสารที่ต้องเตรียม ลำดับการรับรอง และเช็กลิสต์ก่อนยื่น
        เงื่อนไขบางส่วนผันแปรตามสถาบันและประเทศปลายทาง จึงควรตรวจกับหน่วยงานต้นทางก่อนดำเนินการทุกครั้ง
      </p>

      <aside className="mt-6 rounded-lg border border-border bg-muted/40 p-4 text-sm leading-7">{EDU_AUTHORITY_TH}</aside>
      <aside className="mt-3 rounded-lg border border-border bg-muted/40 p-4 text-sm leading-7">{EDU_APOSTILLE_TH}</aside>

      <ul className="mt-8 grid gap-2 sm:grid-cols-2">
        {EDUCATION_DOC_GROUPS.map((g) => (
          <li key={g.slug}>
            <Link to="/education/documents/$group" params={{ group: g.slug }} className="text-primary hover:underline">
              {g.titleTh}
            </Link>
          </li>
        ))}
      </ul>

      {EDUCATION_DOC_GROUPS.map((g) => (
        <section key={g.slug} id={g.slug} className="mt-12 scroll-mt-24">
          <h2 className="text-2xl font-semibold">{g.titleTh}</h2>
          <p className="mt-2 text-muted-foreground">{g.summaryTh}</p>
          <div className="mt-4 space-y-4 leading-8">
            {g.bodyTh.slice(0, 2).map((p, i) => (
              <p key={i}>{p}</p>
            ))}
          </div>
          <Link
            to="/education/documents/$group"
            params={{ group: g.slug }}
            className="mt-3 inline-block text-primary hover:underline"
          >
            อ่านฉบับเต็ม: {g.titleTh} →
          </Link>
        </section>
      ))}

      <p className="mt-10 rounded-lg border border-border bg-muted/30 p-4 text-sm leading-7">
        ส่งภาพสแกนเอกสารมาทาง LINE @nycli โทร +66-83-249-4999 หรืออีเมล nycli@ilc.ltd
        เจ้าหน้าที่จะแจ้งขั้นตอน เอกสารที่ยังขาด และกรอบเวลาที่ทำได้จริงตามกรณีของคุณ
      </p>
          <ServiceAuthorityBlock topic="education" locale="th" />
    </main>
  );
}
